"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateBorrowingBaseMechanics = void 0;
const finley_types_1 = require("@finley-technologies/finley-types");
const concentrationCalcs_1 = require("./concentrationCalcs");
const eligibilityEvaluator_1 = require("./eligibilityEvaluator");
const intermediateCalcEvaluator_1 = require("./intermediateCalcEvaluator");
const advanceRateValidations_1 = require("./receivableAdvanceRate/advanceRateValidations");
const valuationRateEvaluator_1 = require("./valuationRateEvaluator");
const validateBaseFields = (baseFieldConfigs) => {
    // ensure that no fields are duplicated.
    const fieldNameSet = new Set();
    const duplicateField = baseFieldConfigs.find(({ field }) => {
        if (fieldNameSet.has(field)) {
            return true;
        }
        fieldNameSet.add(field);
        return false;
    });
    if (duplicateField) {
        return {
            isValid: false,
            errorMessages: [`field:[${duplicateField.field}] is duplicated`],
        };
    }
    const invalidDateOrDateTimeBaseFields = baseFieldConfigs.reduce((acc, baseField) => {
        const { type } = baseField;
        if (type === 'date' || type === 'optionalDate') {
            if (!finley_types_1.typeguards.datetime.isDateTimeFormatString(baseField.externalDateFormat)) {
                acc.push(`field:[${baseField.field}] has invalid externalDateFormat:[${baseField.externalDateFormat}]`);
            }
        }
        if (type === 'dateTime' || type === 'optionalDateTime') {
            if (!finley_types_1.typeguards.datetime.isDateTimeFormatString(baseField.externalDateTimeFormat)) {
                acc.push(`field:[${baseField.field}] has invalid externalDateTimeFormat:[${baseField.externalDateTimeFormat}]`);
            }
        }
        return acc;
    }, []);
    if (invalidDateOrDateTimeBaseFields.length > 0) {
        return {
            isValid: false,
            errorMessages: invalidDateOrDateTimeBaseFields,
        };
    }
    return {
        isValid: true,
        errorMessages: [],
    };
};
const validateLoanProductMechanics = (loanProduct) => {
    // ensure base fields do not have duplicate definitions.
    const { baseFieldConfigs, intermediateCalcConfigs, externalReceivableIdField, } = loanProduct._config_;
    const { receivableAdvanceRateConfig, receivableValuationRateConfig, eligibilityTestConfigs, } = loanProduct;
    const { errorMessages: baseFieldErrorMessages } = validateBaseFields(baseFieldConfigs);
    // ensure that baseFields has definition for externalReceivableIdField
    const externalReceivableIdFieldConfig = baseFieldConfigs.find(({ field }) => field === externalReceivableIdField);
    if (!externalReceivableIdFieldConfig) {
        baseFieldErrorMessages.push(`baseFields are missing definition for externalReceivableIdField:${externalReceivableIdField}`);
    }
    // intermediate calcs
    const intermediateCalcErrors = (0, intermediateCalcEvaluator_1.checkIntermediateCalcErrors)({
        intermediateCalcs: intermediateCalcConfigs,
        baseFields: baseFieldConfigs.map((baseFieldConfig) => baseFieldConfig.field),
    });
    const intermediateCalcErrorMessages = intermediateCalcErrors.map(({ field, errorMessages }) => `intermediateCalc:[${field}] error:${errorMessages.join(',')}`);
    // eligibility calcs
    const eligibilityCalcErrors = (0, eligibilityEvaluator_1.checkEligibilityConfigErrors)({
        eligibilityCalcs: eligibilityTestConfigs,
        intermediateCalcs: intermediateCalcConfigs,
        baseFields: baseFieldConfigs,
    });
    const eligibilityCalcErrorMessages = eligibilityCalcErrors.map(({ propertyKey, errorMessages }) => `eligibility:[${propertyKey}] errors:${errorMessages.join(',')}`);
    // receivable advance rate formula errors
    const receivableAdvanceRateFormulaErrors = (0, advanceRateValidations_1.checkReceivableAdvanceRateConfigFormulaErrors)({
        receivableAdvanceRateConfig,
        intermediateCalcs: intermediateCalcConfigs,
        baseFields: baseFieldConfigs,
    });
    const receivableAdvanceRateFormulaErrorMessages = receivableAdvanceRateFormulaErrors.map((msg) => `receivableAdvanceRate had an error: ${msg}`);
    // receivable valuation config.
    const receivableValuationRateConfigErrors = (0, valuationRateEvaluator_1.checkReceivableValuationRateConfigErrors)({
        receivableValuationRateConfig,
        intermediateCalcs: intermediateCalcConfigs,
        baseFields: baseFieldConfigs,
    });
    const receivableValuationRateConfigErrorsMessages = receivableValuationRateConfigErrors.map((msg) => `receivableValuationRate had an error: ${msg}`);
    const errorMessages = [
        ...baseFieldErrorMessages,
        ...intermediateCalcErrorMessages,
        ...eligibilityCalcErrorMessages,
        ...receivableAdvanceRateFormulaErrorMessages,
        ...receivableValuationRateConfigErrorsMessages,
    ];
    return {
        isValid: errorMessages.length === 0,
        errorMessages,
    };
};
// validates borrowing base Mechanics configuration
const validateBorrowingBaseMechanics = (borrowingBaseMechanics) => {
    const otherErrorMessages = borrowingBaseMechanics.loanProducts.reduce((prev, loanProduct) => {
        const validResults = validateLoanProductMechanics(loanProduct);
        return prev.concat(validResults.errorMessages);
    }, []);
    // concentration calcs
    const { errorMessages: concentrationCalcsErrors, } = (0, concentrationCalcs_1.checkConcentrationCalcsErrors)(borrowingBaseMechanics);
    // multi-loan product concentration limits
    const { errorMessages: multiLoanProductConcentrationCalcsErrors, } = (0, concentrationCalcs_1.checkMultiLoanProductConcentrationCalcsErrors)(borrowingBaseMechanics);
    // Multi Loan Product Receivable Advance Rates
    const mlpAdvanceRateErrors = (0, advanceRateValidations_1.checkMultiLoanProductAdvanceRateErrors)(borrowingBaseMechanics);
    const errorMessages = otherErrorMessages.concat(concentrationCalcsErrors, multiLoanProductConcentrationCalcsErrors, mlpAdvanceRateErrors);
    return {
        isValid: errorMessages.length === 0,
        errorMessages,
    };
};
exports.validateBorrowingBaseMechanics = validateBorrowingBaseMechanics;
//# sourceMappingURL=validateBorrowingBaseMechanics.js.map